/**
 * AuthProvider – jediný onAuthStateChanged pro celou aplikaci.
 *
 * Drží přihlášeného uživatele, roli z Firestore users/{uid} a isSuperAdmin
 * (role "superadmin" nebo permanentní e-mail z utils/superadmin.ts).
 * Příznak `ready` je false, dokud Firebase nevyhodnotí persistenci – guardy
 * do té doby ukazují spinner místo předčasného redirectu na login.
 *
 * Ze stejného čtení users/{uid} bere i jméno a členské číslo pro účet
 * v hlavičce (profile); obrazovky s useCurrentUser je obnovují svým čtením
 * (utils/authProfile – profil vzniká až dokončením registrace).
 *
 * <RequireAuth> chrání uživatelské routy (nepřihlášený → /login),
 * <RequireRole roles={[...]}> navíc kontroluje roli (jiná role → /user).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Navigate, useLocation } from "react-router-dom";
import { onAuthStateChanged, User } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { PERMANENT_SUPER_ADMIN_EMAIL } from "./superadmin";
import { reportWarning } from "./reportError";
import { waitForSignedInFirestore } from "./completeRegistration";
import {
  ProfileSyncContext,
  profileFromDoc,
  sameProfile,
  type AuthProfile,
  type ProfileStatus,
  type ProfileSync,
} from "./authProfile";

export type { AuthProfile, ProfileStatus } from "./authProfile";

export type UserRole = "user" | "admin" | "superadmin";

export interface AuthState {
  /** Přihlášený uživatel Firebase, nebo null. */
  user: User | null;
  /** True po odhlášení v této relaci – guardy pak nepředávají návratovou cestu. */
  signedOut: boolean;
  /** Role z users/{uid}.role; "user", dokud se nenačte. */
  role: UserRole;
  /** role === "superadmin" nebo permanentní superadmin e-mail. */
  isSuperAdmin: boolean;
  /** True, až Firebase vyhodnotí persistenci A doběhne načtení role. */
  ready: boolean;
  /**
   * Jméno a členské číslo z users/{uid} pro účet v hlavičce; vyplněné jen
   * při profileStatus "loaded".
   */
  profile: AuthProfile | null;
  /** Stav profilu – "unknown" do načtení, po chybě čtení a bez přihlášení. */
  profileStatus: ProfileStatus;
}

const defaultState: AuthState = {
  user: null,
  signedOut: false,
  role: "user",
  isSuperAdmin: false,
  ready: false,
  profile: null,
  profileStatus: "unknown",
};

const AuthContext = createContext<AuthState>(defaultState);

/**
 * Vlastní profil po přihlášení. S jinou otevřenou záložkou aplikace čte
 * Firestore ze serveru přes ni a ta přihlášení převezme až za chvíli – do
 * té doby čtení končí „nemáte oprávnění" (finální pravidla profil bez
 * přihlášení nepustí). Pak se počká, až ho převezme, a čte se znovu.
 */
async function readOwnProfile(uid: string) {
  const ref = doc(db, "users", uid);
  try {
    return await getDoc(ref);
  } catch (err) {
    if ((err as { code?: unknown } | null)?.code !== "permission-denied") throw err;
    await waitForSignedInFirestore(uid);
    return getDoc(ref);
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<AuthState>(defaultState);

  // Čerstvé čtení profilu z obrazovky (useCurrentUser) – jen pro právě
  // přihlášeného; stejné údaje stav nemění (žádné překreslení).
  const syncProfile = useCallback<ProfileSync>((uid, data) => {
    const profile = data ? profileFromDoc(data) : null;
    const profileStatus: ProfileStatus = data ? "loaded" : "missing";
    setState((prev) => {
      if (!prev.user || prev.user.uid !== uid) return prev;
      if (
        prev.profileStatus === profileStatus &&
        sameProfile(prev.profile, profile)
      ) {
        return prev;
      }
      return { ...prev, profile, profileStatus };
    });
  }, []);

  useEffect(() => {
    // Sekvenční čítač: pomalé getDoc role nesmí přepsat stav novější auth
    // události (např. odhlášení během načítání role).
    let seq = 0;
    let wasSignedIn = false;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const my = ++seq;
      if (!user) {
        // Odhlášení (ne studený start): /login pak nesmí dostat state.from,
        // jinak by další přihlášení vedlo na stránku, kde se uživatel odhlásil.
        setState({
          user: null,
          signedOut: wasSignedIn,
          role: "user",
          isSuperAdmin: false,
          ready: true,
          profile: null,
          profileStatus: "unknown",
        });
        return;
      }
      wasSignedIn = true;
      // Uživatele nastavíme HNED (ready až po načtení role – guardy mezitím
      // ukazují spinner). Kdyby zůstal starý stav {user:null, ready:true},
      // RequireAuth by se s onAuthStateChanged AuthPage přetahoval o navigaci
      // (/login ↔ /user smyčka po dobu načítání role).
      setState({
        user,
        signedOut: false,
        role: "user",
        isSuperAdmin: user.email === PERMANENT_SUPER_ADMIN_EMAIL,
        ready: false,
        profile: null,
        profileStatus: "unknown",
      });
      let role: UserRole = "user";
      let profile: AuthProfile | null = null;
      let profileStatus: ProfileStatus = "unknown";
      try {
        const snap = await readOwnProfile(user.uid);
        const data = snap.exists() ? snap.data() : undefined;
        const r = data ? data.role : undefined;
        if (r === "admin" || r === "superadmin") role = r;
        if (data) {
          profile = profileFromDoc(data);
          profileStatus = "loaded";
        } else {
          profileStatus = "missing";
        }
      } catch (err) {
        // Offline nebo výpadek: necháme roli "user" – admin sekce se neotevře,
        // ale aplikace funguje. Role se přenačte při další změně auth stavu.
        reportWarning("useAuth.loadRole", err, { uid: user.uid });
      }
      if (my !== seq) return;
      const isSuperAdmin =
        role === "superadmin" || user.email === PERMANENT_SUPER_ADMIN_EMAIL;
      setState((prev) => {
        // Profil mezitím obnovila obrazovka (syncProfile) – je čerstvější.
        const synced = prev.user === user && prev.profileStatus !== "unknown";
        return {
          user,
          signedOut: false,
          role,
          isSuperAdmin,
          ready: true,
          profile: synced ? prev.profile : profile,
          profileStatus: synced ? prev.profileStatus : profileStatus,
        };
      });
    });
    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={state}>
      <ProfileSyncContext.Provider value={syncProfile}>
        {children}
      </ProfileSyncContext.Provider>
    </AuthContext.Provider>
  );
};

/** Aktuální auth stav (user, role, isSuperAdmin, ready, profile). */
export function useAuth(): AuthState {
  return useContext(AuthContext);
}

/**
 * Návratová cesta pro /login (state.from) – včetně query (?year=…).
 * Po odhlášení se nepředává: další přihlášení má skončit na /user.
 */
function returnState(
  location: { pathname: string; search: string },
  signedOut: boolean
): { from: string } | undefined {
  if (signedOut) return undefined;
  return { from: location.pathname + location.search };
}

/** Celoplošný spinner, než Firebase vyhodnotí persistenci přihlášení. */
const AuthLoading: React.FC = () => (
  <div className="lks-auth-loading" role="status" aria-label="Načítání">
    <div className="spinner-border" style={{ color: "var(--lks-brand)" }} />
  </div>
);

/** Routy jen pro přihlášené: nepřihlášený → /login. */
export const RequireAuth: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, ready, signedOut } = useAuth();
  const location = useLocation();

  if (!ready) return <AuthLoading />;
  if (!user) {
    return (
      <Navigate to="/login" replace state={returnState(location, signedOut)} />
    );
  }
  return <>{children}</>;
};

/** Routy jen pro vybrané role: nepřihlášený → /login, jiná role → /user. */
export const RequireRole: React.FC<{
  roles: UserRole[];
  children: React.ReactNode;
}> = ({ roles, children }) => {
  const { user, role, ready, signedOut } = useAuth();
  const location = useLocation();

  if (!ready) return <AuthLoading />;
  if (!user) {
    return (
      <Navigate to="/login" replace state={returnState(location, signedOut)} />
    );
  }
  if (!roles.includes(role)) return <Navigate to="/user" replace />;
  return <>{children}</>;
};

export function useIsAdmin(): boolean {
  const { role } = useAuth();
  return role === "admin" || role === "superadmin";
}

export default AuthProvider;
